import React, { useEffect, useState } from "react";

// Semantic UI React
import {
  Menu,
  Icon,
  Segment,
  Sidebar,
  Button,
  Checkbox,
} from "semantic-ui-react";

// Styling
import "semantic-ui-css/semantic.min.css";
import "../Shared/Style.css";
import "./style.css";

// ESRI
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Extent from "@arcgis/core/geometry/Extent";
import esriId from "@arcgis/core/identity/IdentityManager";
import Zoom from "@arcgis/core/widgets/Zoom";
import TileLayer from "@arcgis/core/layers/TileLayer";
import * as identify from "@arcgis/core/rest/identify.js";
import IdentifyParameters from "@arcgis/core/rest/support/IdentifyParameters.js";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";

// components
import ZoomWidget from "../GISMappingApp/MapComponents/ZoomWidget";
import SearchWidget from "../Shared/components/SearchWidget";
import useResizeHandler from "../Shared/useResizeHandler";
import Query from "@arcgis/core/rest/support/Query.js";
import * as query from "@arcgis/core/rest/query.js";

// Types
// import { LayerObj } from "./Types";

// List of url substrings to check for
const LINK_VALUES = ["Link", "Image", "PDF", "File", "URL", "Photo"];

const Vanguard = ({ token, config }) => {
  const [view, setView] = useState(null);
  //   const [visibleLayers, setVisibleLayers] = useState<LayerObj | null>();
  const { height } = useResizeHandler();
  const [tabHeight, setTabHeight] = useState(500);
  const [selectedTab, setSelectedTab] = useState("search");
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState<string>("search");
  const [layers, setLayers] = useState([]);

  const [initialExtent, setInitialExtent] = useState(
    new Extent({
      xmin: 2269629.95936241,
      ymin: 300351.17046743,
      xmax: 2298036.20936241,
      ymax: 313991.79546743,
      spatialReference: {
        wkid: 3418,
      },
    })
  );

  // console.log("Vanguard", {
  //   token,
  //   config,
  //   initialExtent,
  //   tabHeight,
  // });

  useEffect(() => {
    const menu_height = document.getElementById("sidebar-menu").offsetHeight;
    setTabHeight(height - menu_height);
  }, [height]);

  useEffect(() => {
    if (view) {
      // mapview click
      view.on("click", (e) => {
        view.graphics.removeAll();
        executeIdentifyTask(e.mapPoint, view.extent, view.height, view.width);
      });

      reactiveUtils.watch(
        () => view.popup.selectedFeature,
        (graphic) => {
          if (graphic) {
            // console.error({ graphic });

            // clone the graphic
            var selected_graphic = graphic.clone();

            // remove all map graphics
            view.graphics.removeAll();

            // symbology
            if (selected_graphic.geometry.type == "polygon") {
              selected_graphic.symbol = {
                type: "simple-fill",
                outline: {
                  width: 3,
                  color: [255, 0, 0, 0.8],
                  cap: "round",
                  join: "round",
                },
                color: [26, 26, 26, 0.17],
              };
            } else if (selected_graphic.geometry.type == "point") {
              selected_graphic.symbol = {
                type: "simple-marker",
                outline: { width: 2, color: [255, 0, 0, 1] },
                size: 20,
                color: [255, 255, 255, 0],
              };
            } else if (selected_graphic.geometry.type == "polyline") {
              selected_graphic.symbol = {
                type: "simple-line",
                width: 4,
                color: [255, 0, 0, 1],
              };
            }

            // Add graphic to map
            view.graphics.add(selected_graphic);
          }
        }
      );
    }
  }, [view]);

  useEffect(() => {
    // async function initialize() {
    // Load ESRI's IdentifyManager and add the token
    esriId.registerToken({
      server: config.server_url,
      token: config.token,
    });

    var basemap = new TileLayer({
      url: config.default.basemap_url,
      visible: true,
      id: "basemap",
      title: "basemap",
      minScale: 409600,
      maxScale: 200,
    });

    var sc = new TileLayer({
      url: config.default.sc_url,
      id: "Surrounding Counties",
      title: "Surrounding Counties",
      visible: true,
      minScale: 409600,
      maxScale: 200,
    });

    const road_labels = new TileLayer({
      url: config.default.road_labels_url,
      id: "Road Labels",
      title: "Road Labels",
      visible: true,
      minScale: 409600,
      maxScale: 200,
    });

    const landuse = new TileLayer({
      url: config.layers.landuse_url,
      id: "landuse",
      title: "Landuse",
      visible: true,
      minScale: 409600,
      maxScale: 200,
    });

    const future_landuse = new TileLayer({
      url: config.layers.future_landuse_url,
      id: "future_landuse",
      title: "Future Landuse",
      visible: true,
      minScale: 409600,
      maxScale: 200,
    });

    const zoning = new TileLayer({
      url: config.layers.zoning_url,
      id: "zoning",
      title: "Zoning",
      visible: true,
      minScale: 409600,
      maxScale: 200,
    });

    let map = new Map({
      layers: [basemap, sc, road_labels, landuse, future_landuse, zoning],
    });

    let mapView = new MapView({
      container: "map-container",
      map: map,
      extent: initialExtent,
      popup: {
        dockEnabled: false,
        dockOptions: {
          buttonEnabled: false,
          breakpoint: false,
        },
      },
      constraints: {
        rotationEnabled: false,
        minScale: 409600,
        maxScale: 200,
        minZoom: 0,
        maxZoom: 11,
      },
    });
    mapView.popup.dockOptions = {
      buttonEnabled: false,
    };
    mapView.popup.dockEnabled = false;
    mapView.popup.highlightEnabled = true;

    mapView.when(() => {
      new Zoom({
        view: mapView,
      });

      setLayers([landuse, future_landuse, zoning]);

      setView(mapView);
    });
  }, []);

  const executeIdentifyTask = (mapPoint, extent, height, width) => {
    let tasks = [];

    if (layers[0].visible) {
      const land_use_params = new IdentifyParameters();
      land_use_params.tolerance = 3;
      land_use_params.layerIds = [0, 1, 2, 3, 4, 5];
      land_use_params.layerOption = "visible";
      land_use_params.width = width;
      land_use_params.height = height;
      land_use_params.geometry = mapPoint;
      land_use_params.mapExtent = extent;
      land_use_params.returnFieldName = false;
      land_use_params.returnGeometry = true;

      tasks[0] = identify.identify(config.layers.landuse_url, land_use_params);
    } else {
      tasks[0] = new Promise((resolve) => resolve({ results: [] }));
    }

    if (layers[1].visible) {
      const future_land_use_params = new IdentifyParameters();
      future_land_use_params.tolerance = 3;
      future_land_use_params.layerIds = [1];
      future_land_use_params.layerOption = "visible";
      future_land_use_params.width = width;
      future_land_use_params.height = height;
      future_land_use_params.geometry = mapPoint;
      future_land_use_params.mapExtent = extent;
      future_land_use_params.returnFieldName = false;
      future_land_use_params.returnGeometry = true;

      tasks[1] = identify.identify(
        config.layers.future_landuse_url,
        future_land_use_params
      );
    } else {
      tasks[1] = new Promise((resolve) => resolve({ results: [] }));
    }

    if (layers[2].visible) {
      const zoning_params = new IdentifyParameters();
      zoning_params.tolerance = 3;
      zoning_params.layerIds = [0];
      zoning_params.layerOption = "visible";
      zoning_params.width = width;
      zoning_params.height = height;
      zoning_params.geometry = mapPoint;
      zoning_params.mapExtent = extent;
      zoning_params.returnFieldName = false;
      zoning_params.returnGeometry = true;

      tasks[2] = identify.identify(config.layers.zoning_url, zoning_params);
    } else {
      tasks[2] = new Promise((resolve) => resolve({ results: [] }));
    }

    Promise.all(tasks).then((result) => {
      // add url to result layers
      result[0].results.map((record) => {
        // record.url = config.layers.landuse_url;
        record.name = "Land Use";
        return record;
      });
      result[1].results.map((record) => {
        // record.url = config.layers.future_landuse_url;
        record.name = "Future LandUse";
        return record;
      });
      result[2].results.map((record) => {
        // record.url = config.layers.zoning_url;
        record.name = "Zoning";
        return record;
      });

      // Get the results of the two queries
      var results = result[0].results
        .concat(result[1].results)
        .concat(result[2].results)
        .sort((a, b) => {
          // Use toUpperCase() to ignore character casing
          const layerA = a.layerName.toUpperCase();
          const layerB = b.layerName.toUpperCase();

          let comparison = 0;
          if (layerA > layerB) {
            comparison = 1;
          } else if (layerA < layerB) {
            comparison = -1;
          }
          return comparison;
        })
        .map((feature, index) => {
          feature.index = index;
          feature.label = feature.feature.attributes[feature.displayFieldName];
          return feature;
        });

      renderPopup(results);
    });
  };

  const renderPopup = (results) => {
    // console.log(results);

    view.closePopup();

    const promises = results.map((result) => {
      return new Promise((resolve, reject) => {
        // console.log(result);

        var feature = result.feature;
        var layerName = result.name;

        if (feature.attributes.name === "Parcels") {
          feature.popupTemplate = {
            title: `Parcel: ${feature.attributes.PARCEL_NO}`,
            content: `
            <table class="esri-widget__table" summary="List of attributes and values">
              <tbody>
                <tr>
                  <th style="width: 25%">Owner</th>
                  <td>${feature.attributes.OwnerName}</td>
                </tr>
                <tr>
                  <th style="width: 25%">Address</th>
                  <td>${feature.attributes.FullSitus}</td>
                </tr>
              </tbody>
            </table>`,
          };
        } else {
          feature.popupTemplate = {
            title: layerName,
            content: () => {
              var html = `<table class="esri-widget__table"><tbody>`;
              // console.error(feature.attributes);
              for (var key in feature.attributes) {
                // Check if the field name includes an indicator for a link
                // LINK_VALUES
                let link_value_match = key
                  .split(" ")
                  .find((term) => LINK_VALUES.includes(term));

                // Skip if the key is in the blacklist
                if (
                  ["objectid", "id", "fid", "shape", "layername"].includes(
                    key.toLowerCase()
                  )
                ) {
                  continue;
                }

                // Add it as a new row in the table
                html += `<tr>`;
                html += `<td class="third">${key}</td>`;

                if (
                  link_value_match &&
                  feature.attributes[key].includes("http")
                ) {
                  html += `<td class="two-third">
                    <a class="ui fluid button" href="${feature.attributes[key]}" role="button">
                      <i aria-hidden="true" class="external icon"></i>
                      View ${link_value_match}
                    </a>
                  </td>`;
                } else {
                  html += `<td class="two-third">${
                    feature.attributes[key]
                    // ? feature.attributes[key]
                    // : "Not Available"
                  }</td>`;
                }
                html += `</tr>`;
              }
              html += `</tbody></table>`;
              return html;
            },
          };
        }

        resolve(feature);
      });
    });

    Promise.all(promises).then((features) => {
      if (features.length > 0) {
        // console.log({ view });
        view.openPopup({
          features: features,
        });
      }
    });
  };

  const toggleVisibility = (layer: any, checked: boolean | undefined) => {
    layer.visible = checked;

    // // update the visibility
    // sublayer.visible = checked;

    // // @ts-ignore
    // console.log(view!.map.layers.items);

    // // @ts-ignore
    // const match = view!.map.layers.items.find(
    //   (layer: Layer) => layer.title === `${sublayer.title} Tile`
    // );

    // console.log({ match });
    // if (match) {
    //   match.visible = checked;
    // }
  };

  const zoomToExtent = (feature) => {
    // expand the extent and override the spatial reference because esri sucks
    var ext = feature.geometry.extent.clone().expand(1.25);
    ext.spatialReference.wkid = 3418;

    // Zoom to the extent
    view.goTo(ext);
  };

  const searchCallback = (record: any) => {
    // console.log("searchCallback", { record });

    if (!view) {
      return false;
    }

    // Check if the width of the app is less than 800 and if so then close the sidebar
    if (window.innerWidth <= 800) {
      setSidebarVisible(false);
    }

    const queryObj = new Query();
    queryObj.returnGeometry = true;
    queryObj.outFields = ["*"];
    queryObj.where = `${record.field} = '${record.value}' AND UPPER(SitusCityN)='WEST BURLINGTON'`;

    view.closePopup();

    query.executeQueryJSON(config.parcels_url!, queryObj).then(
      (results) => {
        // prints the array of result graphics to the console
        // console.error(results.features);

        view.closePopup();

        const features = results.features.map((feature) => {
          // console.log({ feature });
          feature.attributes.name = "Parcels";
          return {
            feature: feature,
            layerName: "Parcels",
          };
        });

        // zoom to extent
        zoomToExtent(features[0].feature);

        view.popup.location = features[0].feature.geometry.extent.center;

        // render the popup
        renderPopup(features);
        // view.goTo(results.features[0]);

        // view.openPopup({
        //   // @ts-ignore
        //   location: results.features[0].geometry.centroid,
        //   title: `Parcel: ${results.features[0].attributes.PARCEL_NO}`,
        //   content: `
        //     <table class="esri-widget__table" summary="List of attributes and values">
        //       <tbody>
        //         <tr>
        //           <th style="width: 25%">Owner</th>
        //           <td>${results.features[0].attributes.OwnerName}</td>
        //         </tr>
        //         <tr>
        //           <th style="width: 25%">Address</th>
        //           <td>${results.features[0].attributes.FullSitus}</td>
        //         </tr>
        //       </tbody>
        //     </table>`,
        // });
      },
      (error) => {
        console.log(error); // will print error in console, if any
      }
    );
  };

  return (
    <>
      <Sidebar.Pushable
        as={Segment}
        id="site-pushable"
        attached
        style={{ height: "100%" }}
      >
        <Sidebar
          as={Segment}
          id="vanguard_sidebar"
          direction="right"
          animation="overlay"
          onHide={() => setSidebarVisible(false)}
          vertical
          visible={sidebarVisible}
          width="wide"
          style={{
            overflowY: "hidden !important",
            textAlign: "left",
            backgroundColor: "#FFFFFF",
            padding: 0,
          }}
        >
          <Menu widths={2} style={{ margin: 0 }} id="sidebar-menu">
            <Menu.Item
              name="search"
              active={selectedTab === "search"}
              onClick={() => setActiveMenuItem("search")}
            />
            <Menu.Item
              name="layers"
              active={selectedTab === "layers"}
              onClick={() => setActiveMenuItem("layers")}
            />
          </Menu>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              border: "1px solid #d4d4d5",
              borderTop: "none",
              padding: "0.5em",
              overflow: "hidden",
            }}
          >
            <Button
              icon
              onClick={() => setSidebarVisible(false)}
              style={{ position: "absolute", top: "4px", right: "2px" }}
            >
              <Icon name="remove" />
            </Button>
            {activeMenuItem === "search" && (
              <SearchWidget
                city_scope="WEST BURLINGTON"
                service={config.parcels_url}
                result_callback={searchCallback}
              />
            )}
            {activeMenuItem === "layers" && (
              <div>
                {layers.map((layer, index) => {
                  return (
                    <Checkbox
                      key={index}
                      defaultChecked={layer.visible}
                      style={{ width: "100%", marginBottom: "10px" }}
                      label={layer.title}
                      onClick={(_e, { checked }) =>
                        toggleVisibility(layer, checked)
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
        </Sidebar>
        <Sidebar.Pusher id="site-pusher">
          <Segment id="site-container">
            <div id="map-container">
              <div className="top-left map-buttons">
                {view && <ZoomWidget view={view} />}
              </div>
              <div className="top-right map-buttons">
                <Button onClick={() => setSidebarVisible(true)} icon>
                  <Icon name="list" />
                </Button>
              </div>
            </div>
          </Segment>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </>
  );
};

export default Vanguard;
